import React, { BaseSyntheticEvent, FC, useEffect, useState } from "react";
import { CloseButton, Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import ReactSelect from "../../../../components/inputs/select";
import { StyledInput } from "../../../../components/inputs/styledInput";
import L from "leaflet";
import { MapContainer, TileLayer } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { LocationMarker } from "./LocationMarker";
import { DraggableMarker } from "./DraggebleMarker";
import {
  useCitiesByCountryQuery,
  useCountriesQuery,
} from "../../../../graphql/country.hooks";
import { SelectOption } from "../../../../types";
import { LocationItem } from "../../types";

L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

type LocationWithIndex = LocationItem & {
  index: number;
};

type Props = {
  show: boolean;
  onConfirm: (
    location: Partial<LocationItem>,
    editingIndex: number | null
  ) => void;
  onCancel?: () => void;
  location?: LocationWithIndex | null;
};

export const LocationModal: FC<Props> = ({
  show,
  location,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();

  const [selectedCountry, setSelectedCountry] = useState<number>(null);
  const [selectedCity, setSelectedCity] = useState<number>(null);
  const [position, setPosition] = useState<[number, number] | null>(null);
  const [latitude, setLatitude] = useState<number>(null);
  const [longitude, setLongitude] = useState<number>(null);
  const [rangeKm, setRangeKm] = useState<number>(null);

  const [cities, setCities] = useState<SelectOption[]>([]);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [hasMoreCities, setHasMoreCities] = useState(true);
  const [citiesPage, setCitiesPage] = useState<{
    afterId: number | null;
    afterValue: string | null;
  }>({ afterId: null, afterValue: null });

  const { data: contriesData } = useCountriesQuery();

  const { fetchMore } = useCitiesByCountryQuery({
    variables: {
      countryId: selectedCountry,
      limit: 50,
      afterId: citiesPage.afterId,
      afterValue: citiesPage.afterValue,
      order: "ASC",
      field: "title",
    },
    skip: !selectedCountry,
  });

  const disabledAddLocationBtn = !position && !selectedCountry;

  const countriesOptions = contriesData?.countries?.map((contry) => ({
    label: contry.title,
    value: contry.id,
  }));

  useEffect(() => {
    if (!location) return;
    setSelectedCountry(location?.countryId);
    setSelectedCity(location?.cityId);
    setRangeKm(location?.radius);
    setLatitude(location?.latitude);
    setLongitude(location?.longitude);
    setPosition([location?.latitude, location?.longitude]);
  }, [location]);

  useEffect(() => {
    if (position?.[0] || position?.[1]) {
      setLatitude(position[0]);
      setLongitude(position[1]);
      setSelectedCity(null);
      setSelectedCountry(null);
    } else if (position === null) {
      setLatitude(null);
      setLongitude(null);
    }
  }, [position]);

  useEffect(() => {
    if (!selectedCountry) {
      setCities([]);
      setHasMoreCities(true);
      setCitiesPage({ afterId: null, afterValue: null });
      return;
    }

    setCitiesLoading(true);
    fetchMore({
      variables: {
        countryId: selectedCountry,
        limit: 50,
        afterId: null,
        afterValue: null,
      },
    }).then((res) => {
      const newCities = res.data.citiesByCountry.map((c) => ({
        label: c.title,
        value: c.id,
      }));
      setCities(newCities);
      setHasMoreCities(newCities.length === 50);
      setCitiesPage({
        afterId: newCities[newCities.length - 1]?.value || null,
        afterValue: newCities[newCities.length - 1]?.label || null,
      });
      setCitiesLoading(false);
    });
  }, [selectedCountry, fetchMore]);

  const handleLatChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value;
    if (Number(val) > 90) val = "90";
    if (Number(val) < -90) val = "-90";
    setPosition([Number(val), position ? position[1] : 0]);
  };

  const handleLngChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value;
    if (Number(val) > 180) val = "180";
    if (Number(val) < -180) val = "-180";
    setPosition([position ? position[0] : 0, Number(val)]);
  };

  const handleRangeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value;
    if (Number(val) < 0) val = "0";
    setRangeKm(+val);
  };

  const resetValues = () => {
    setSelectedCountry(null);
    setSelectedCity(null);
    setPosition(null);
    setRangeKm(null);
    setLatitude(null);
    setLongitude(null);
  };

  const closeModal = () => {
    resetValues();
    onCancel();
  };

  const closeByBackground = (e: BaseSyntheticEvent) => {
    e.stopPropagation();
    if (e.target.id === "confirmModal") closeModal();
  };

  const tryAddNewLocation = () => {
    const locationData = {
      countryId: selectedCountry,
      cityId: selectedCity,
      longitude,
      latitude,
      radius: rangeKm,
    };

    onConfirm(locationData, location ? location.index : null);
  };

  const fetchCities = async (params: {
    countryId: number;
    afterId?: number | null;
    afterValue?: string | null;
    search?: string | null;
    append?: boolean;
  }) => {
    if (!params.countryId) return;

    setCitiesLoading(true);

    try {
      const res = await fetchMore({
        variables: {
          countryId: params.countryId,
          limit: 50,
          afterId: params.afterId || null,
          afterValue: params.afterValue || null,
          search: params.search || null,
        },
      });

      const newCities = res.data.citiesByCountry.map((c) => ({
        label: c.title,
        value: c.id,
      }));

      setCities((prev) =>
        params.append ? [...prev, ...newCities] : newCities
      );
      setHasMoreCities(newCities.length === 50);
      setCitiesPage({
        afterId: newCities[newCities.length - 1]?.value || null,
        afterValue: newCities[newCities.length - 1]?.label || null,
      });
    } finally {
      setCitiesLoading(false);
    }
  };

  return (
    <div
      id="confirmModal"
      className={`modal ${show ? "show d-flex" : ""} align-items-center justify-content-center`}
      style={{
        display: show ? "block" : "none",
        background: "rgb(0, 0, 0, 0.6)",
      }}
      onClick={(e) => closeByBackground(e)}
    >
      <div className="col modal-dialog modal-dialog-scrollable">
        <div className="modal-content text-break" style={{ maxWidth: "500px" }}>
          <div className="modal-header border-0 d-flex justify-content-between">
            <h5 className="modal-title fw-700 fs-3">
              {t("page.Add Location")}
            </h5>
            <CloseButton
              onClick={closeModal}
              style={{ height: "16px", width: "16px", opacity: "1" }}
            />
          </div>
          <div className="modal-body border-0 py-3 px-4">
            <ReactSelect
              className="mb-3 text-dark-gray"
              options={countriesOptions}
              value={
                countriesOptions?.find(
                  (country) => country.value === selectedCountry
                ) || ""
              }
              onChange={(option: SelectOption) => {
                setSelectedCountry(option?.value);
                setPosition(null);
              }}
              label={t("profile.country")}
              placeholder={t("page.Select Country")}
            />
            <ReactSelect
              isDisabled={!selectedCountry}
              options={cities}
              value={cities.find((c) => c.value === selectedCity) || ""}
              onChange={(option: SelectOption) =>
                setSelectedCity(option?.value)
              }
              onInputChange={(inputValue, { action }) => {
                if (action === "input-change") {
                  fetchCities({
                    countryId: selectedCountry,
                    search: inputValue,
                    append: false,
                  });
                }
              }}
              onMenuScrollToBottom={() => {
                if (citiesLoading || !hasMoreCities) return;
                fetchCities({
                  countryId: selectedCountry,
                  afterId: citiesPage.afterId,
                  afterValue: citiesPage.afterValue,
                  append: true,
                });
              }}
              label={t("page.city")}
              placeholder={t("page.Select City")}
              isClearable
              filterOption={null}
            />
            <div className="d-flex align-items-center my-3">
              <div
                className="flex-grow-1 border-none bg-light"
                style={{ height: "1px" }}
              ></div>
              <span className="mx-3 text-gray fs-7">or</span>
              <div
                className="flex-grow-1 border-none bg-light"
                style={{ height: "1px" }}
              ></div>
            </div>
            <Row className="g-3 mb-3">
              <Col xs={12} md={6}>
                <div className="form-floating">
                  <StyledInput
                    type="number"
                    name="latitudeRange"
                    className="numberForm text-dark-gray"
                    label={t("page.Latitude Range")}
                    error={false}
                    value={latitude || ""}
                    onChange={handleLatChange}
                  />
                </div>
              </Col>
              <Col xs={12} md={6}>
                <div className="form-floating">
                  <StyledInput
                    type="number"
                    name="longitudeRange"
                    className="numberForm text-dark-gray"
                    label={t("page.Longitude Range")}
                    error={false}
                    value={longitude || ""}
                    onChange={handleLngChange}
                  />
                </div>
              </Col>
            </Row>
            <div className="form-floating mb-3">
              <StyledInput
                type="number"
                name="rangeKm"
                className="numberForm text-dark-gray"
                label={t("page.Range in km")}
                error={false}
                value={rangeKm || ""}
                onChange={handleRangeChange}
              />
            </div>
            {show && (
              <MapContainer
                center={[50.45, 30.52]}
                zoom={7}
                style={{ height: "220px", width: "100%" }}
              >
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

                {position && (
                  <DraggableMarker
                    position={position}
                    setPosition={setPosition}
                  />
                )}
                <LocationMarker setPosition={setPosition} />
              </MapContainer>
            )}
          </div>
          <Row className="p-12 g-2">
            <Col xs={6}>
              <button
                type="button"
                className="btn btn-outline-dark dark-hover text-dark-gray w-100"
                onClick={closeModal}
              >
                {t("profile.cancel")}
              </button>
            </Col>
            <Col xs={6}>
              <button
                type="button"
                disabled={disabledAddLocationBtn}
                className="white-hover btn btn-primary text-white w-100"
                onClick={() => tryAddNewLocation()}
              >
                {t("page.Add Location")}
              </button>
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
};
